import { z } from "zod";

import {
  VersionResolutionWarningSchema,
  VersionStringSchema,
  ViewManifestSchema,
} from "../manifest/schemas";
import { requiredUnknown } from "../schema-utils";
import { FlowMetaSchema } from "../flow/schemas";
import { ModuleRefSchema, WireModuleRefSchema } from "../view/schemas";
import type {
  DataFetcherWarning,
  LoadedViewResponse,
  LoaderAssetsChunk,
  LoaderAssetsPayload,
  LoaderErrorCode,
  LoaderErrorResponse,
  LoaderResponse,
  LoaderStreamChunk,
  LoaderTerminalChunk,
  ModuleVersionWarning,
  RedirectResponse,
  ResolvedNavigationTarget,
  ResponseWarning,
  ViewResponse,
  ViewVersionWarning,
  WireEarlyLayout,
  WireLoadingModule,
} from "./types";
import { LOADER_STREAM_PROTOCOL_VERSION } from "./types";

// ============================================================================
// LoadedViewResponse (wire shape)
// ============================================================================

/**
 * The wire-safe identity of the matched view as shipped inside a
 * {@link ViewResponse}. Contains only the serializable fields the client
 * needs — `viewId`, `viewVersion`, and the view-level manifest. The full
 * `View` definition is intentionally absent; the client imports the view
 * module fresh from the CDN.
 */
export const LoadedViewResponseSchema = z.object({
  viewId: z.string(),
  viewVersion: VersionStringSchema,
  manifest: ViewManifestSchema,
}) satisfies z.ZodType<LoadedViewResponse>;

// ============================================================================
// Warnings
// ============================================================================

export const DataFetcherWarningSchema = z.object({
  kind: z.literal("dataFetcher"),
  source: z.literal("view"),
  error: z.string(),
}) satisfies z.ZodType<DataFetcherWarning>;

export const ViewVersionWarningSchema = z.object({
  kind: z.literal("view"),
  viewId: z.string(),
  warning: VersionResolutionWarningSchema,
  resolvedVersion: VersionStringSchema,
}) satisfies z.ZodType<ViewVersionWarning>;

export const ModuleVersionWarningSchema = z.object({
  kind: z.literal("module"),
  moduleId: z.string(),
  warning: VersionResolutionWarningSchema,
  resolvedVersion: VersionStringSchema,
}) satisfies z.ZodType<ModuleVersionWarning>;

/**
 * Any warning that may appear in the `warnings` array on a
 * {@link ViewResponse}. Discriminated by `kind`.
 */
export const ResponseWarningSchema = z.discriminatedUnion("kind", [
  ViewVersionWarningSchema,
  ModuleVersionWarningSchema,
  DataFetcherWarningSchema,
]) satisfies z.ZodType<ResponseWarning>;

// ============================================================================
// Resolved Navigation Targets
// ============================================================================

/**
 * Mirror of {@link ResolvedNavigationTarget}. Discriminated on `kind` so the
 * three variants (`view` / `external` / `back`) can be parsed with no
 * ambiguity from the wire.
 */
export const ResolvedNavigationTargetSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("view"),
    route: z.string(),
    query: z.record(z.string()).optional(),
    persistSearch: z.boolean().optional(),
  }),
  z.object({ kind: z.literal("external"), url: z.string() }),
  z.object({ kind: z.literal("back") }),
]) satisfies z.ZodType<ResolvedNavigationTarget>;

// ============================================================================
// Loader error code
// ============================================================================

export const LoaderErrorCodeSchema = z.enum([
  "no_flow_match",
  "branch_not_found",
  "views_load_failed",
  "no_view_match",
  "ambiguous_route",
  "module_load_failed",
  "auth_failed",
  "view_not_found",
  "redirect_loop",
  "add_response_headers_failed",
  "internal_error",
]) satisfies z.ZodType<LoaderErrorCode>;

// ============================================================================
// Loader responses
// ============================================================================

/**
 * Terminal success response — the winning view and its data.
 *
 * `viewData` and `moduleDataMap` entries use {@link requiredUnknown} so that
 * Zod does not misinfer them as optional (see schema-utils.ts).
 */
export const ViewResponseSchema = z.object({
  type: z.literal("view"),
  loadedView: LoadedViewResponseSchema,
  viewData: requiredUnknown(),
  moduleDataMap: z.record(z.string(), requiredUnknown()),
  layoutModuleRef: WireModuleRefSchema,
  layoutProps: z.record(z.string(), z.unknown()).optional(),
  resolvedModuleVersions: z.record(VersionStringSchema),
  warnings: z.array(ResponseWarningSchema),
  resolvedInteractions: z.record(ResolvedNavigationTargetSchema),
  pathParams: z.record(z.string()),
  flowMeta: FlowMetaSchema,
}) satisfies z.ZodType<ViewResponse>;

export const RedirectResponseSchema = z.object({
  type: z.literal("redirect"),
  location: z.string(),
  external: z.boolean().optional(),
}) satisfies z.ZodType<RedirectResponse>;

export const LoaderErrorResponseSchema = z.object({
  type: z.literal("error"),
  status: z.number(),
  message: z.string(),
  code: LoaderErrorCodeSchema,
  errorView: ModuleRefSchema.optional(),
  errorContext: z
    .object({
      errorType: z.string(),
      moduleId: z.string().optional(),
    })
    .optional(),
  flowMeta: FlowMetaSchema.optional(),
  errorModuleData: z.unknown().optional(),
}) satisfies z.ZodType<LoaderErrorResponse>;

/**
 * Discriminated union over every loader response shape emitted by
 * `handleLoader` on the server.
 */
export const LoaderResponseSchema = z.discriminatedUnion("type", [
  ViewResponseSchema,
  RedirectResponseSchema,
  LoaderErrorResponseSchema,
]) satisfies z.ZodType<LoaderResponse>;

// ============================================================================
// Progressive loader stream chunks (opt-in NDJSON)
// ============================================================================

export const WireLoadingModuleSchema = z.object({
  moduleId: z.string().min(1),
  version: VersionStringSchema,
  props: z.record(z.string(), z.unknown()),
}) satisfies z.ZodType<WireLoadingModule>;

export const WireEarlyLayoutSchema = WireLoadingModuleSchema.extend({
  data: requiredUnknown(),
}) satisfies z.ZodType<WireEarlyLayout>;

export const LoaderAssetsPayloadSchema = z.object({
  loadedView: LoadedViewResponseSchema,
  layoutModuleRef: WireModuleRefSchema,
  resolvedModuleVersions: z.record(VersionStringSchema),
  moduleRefs: z.array(WireModuleRefSchema),
  loadingModule: WireLoadingModuleSchema.optional(),
  earlyLayout: z.boolean().optional(),
  layout: WireEarlyLayoutSchema.optional(),
  flowMeta: FlowMetaSchema.optional(),
}) satisfies z.ZodType<LoaderAssetsPayload>;

export const LoaderAssetsChunkSchema = z.object({
  type: z.literal("assets"),
  protocolVersion: z.literal(LOADER_STREAM_PROTOCOL_VERSION),
  payload: LoaderAssetsPayloadSchema,
}) satisfies z.ZodType<LoaderAssetsChunk>;

export const LoaderTerminalChunkSchema = z.object({
  type: z.literal("terminal"),
  protocolVersion: z.literal(LOADER_STREAM_PROTOCOL_VERSION),
  status: z.number(),
  response: LoaderResponseSchema,
}) satisfies z.ZodType<LoaderTerminalChunk>;

export const LoaderStreamChunkSchema = z.discriminatedUnion("type", [
  LoaderAssetsChunkSchema,
  LoaderTerminalChunkSchema,
]) satisfies z.ZodType<LoaderStreamChunk>;
